import React, { useEffect, useRef, useState, MouseEvent } from "react";
import classNames from "classnames";
import { TypewriterText } from "../../common/components/TypewriterText";
import { MatrixHero } from "../../common/components/MatrixHero";
import "../../scss/environment.scss";
import { useI18n } from "../../i18n/I18nProvider";

// ==================== HOOKS ====================

function useInView(threshold = 0.15) {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (ref.current) observer.unobserve(ref.current);
                }
            },
            { threshold }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [threshold]);
    return { ref, inView };
}

// ==================== DATA ====================

const INTRO_TEXT =
    "Centrum Cyberbezpieczeństwa rozwija współpracę z otoczeniem społeczno-gospodarczym, wspierając organizacje w rozwiązywaniu problemów związanych z bezpieczeństwem cyfrowym. Zapraszamy przedsiębiorstwa, instytucje publiczne oraz organizacje do zgłaszania wyzwań i zagadnień, które mogą zostać przeanalizowane i opracowane w ramach wspólnych działań z naszym zespołem.";

const COOPERATION_TEXT =
    "Współpraca może obejmować realizację analiz, szkoleń, projektów badawczych, opracowań eksperckich, projektów studenckich oraz prac dyplomowych, których celem jest wypracowanie praktycznych rozwiązań wspierających rozwój cyberbezpieczeństwa w organizacjach.";

const COOPERATION_AREAS = [
    "analizy i audyty bezpieczeństwa",
    "szkolenia z cyberbezpieczeństwa",
    "projekty badawcze",
    "opracowania eksperckie",
    "projekty studenckie i prace dyplomowe",
];

const CONTACT_EMAIL = "cc@wsiz.edu.pl";

// ==================== COMPONENT ====================

const EnvironmentPage: React.FC = () => {
    const [ready, setReady] = useState(false);
    const { t } = useI18n();
    const cooperationAreas = [
        t("environment_area_1"), t("environment_area_2"), t("environment_area_3"),
        t("environment_area_4"), t("environment_area_5"), t("environment_area_6"),
    ];
    const { ref: refIntro, inView: inViewIntro } = useInView(0.15);
    const { ref: refCooperation, inView: inViewCooperation } = useInView(0.15);
    const { ref: refAreas, inView: inViewAreas } = useInView(0.15);
    const { ref: refCta, inView: inViewCta } = useInView(0.15);

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
    }, []);

    const handleMouseMove = (e: MouseEvent<HTMLElement>) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        target.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
        target.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    };

    return (
        <section
            className={classNames("env-page", { "env-page--ready": ready })}
            onMouseMove={handleMouseMove}
            aria-label={t("environment_section_label")}
        >
            <div className="grid-overlay" aria-hidden="true" />
            <div className="grid-glow" aria-hidden="true" />

          
            <MatrixHero
                title={t("environment_hero_title")}
                subtitle={t("environment_hero_subtitle")}
                density={0.5}
            />

            <div className="env-page__inner">

              
                <article
                    ref={refIntro}
                    className={classNames("env-card", { "env-card--visible": inViewIntro })}
                >
                    <div className="env-card__glow" aria-hidden="true" />
                    <h2 className="env-card__title">{t("environment_intro_title")}</h2>
                    <p className="env-card__text">
                        <TypewriterText text={t("environment_intro_text")} active={inViewIntro} speed={4} />
                    </p>
                </article>

              
                <article
                    ref={refCooperation}
                    className={classNames("env-card env-card--accent", { "env-card--visible": inViewCooperation })}
                >
                    <div className="env-card__glow" aria-hidden="true" />
                    <h2 className="env-card__title">{t("environment_cooperation_title")}</h2>
                    <p className="env-card__text">
                        <TypewriterText text={t("environment_cooperation_text")} active={inViewCooperation} speed={4} />
                    </p>
                </article>

              
                <div
                    ref={refAreas}
                    className={classNames("env-areas", { "env-areas--visible": inViewAreas })}
                >
                    <h2 className="env-areas__title">{t("environment_areas_title")}</h2>
                    <div className="env-areas__grid">
                        {cooperationAreas.map((area, i) => (
                            <div
                                key={i}
                                className="env-tile"
                                style={{ animationDelay: `${i * 0.1}s` }}
                            >
                                <span className="env-tile__text">{area}</span>
                            </div>
                        ))}
                    </div>
                </div>

              
                <div
                    ref={refCta}
                    className={classNames("env-cta", { "env-cta--visible": inViewCta })}
                >
                    <h2 className="env-cta__heading">{t("environment_cta_heading")}</h2>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="env-cta__email">
                        {CONTACT_EMAIL}
                    </a>
                </div>

            </div>
        </section>
    );
};

export default EnvironmentPage;
